/**
 * GH-923 — is this browser tab an Ops Panel impersonation ("support mode") tab?
 *
 * Persisted stores read this ONCE, at module load and before their first
 * hydration, to pick their storage: a support-mode tab persists to per-tab
 * sessionStorage under its own keys, never to the shared localStorage keys
 * every other open tab of the operator's browser reads (and, for the auth
 * store, watches via `storage` events — axios.config.ts's cross-tab sync).
 * See scopeAuthStoreToImpersonationTab (useAuthStore.ts) for the full story.
 *
 * Deliberately dependency-free: stores import it at module-evaluation time.
 */

/**
 * OPS-3.5 — the Ops Panel handoff lands on `${ADMIN_URL}?impersonation_code=<code>`:
 * a single-use, 120-second code root.tsx's AuthInitializer exchanges for the
 * impersonation token (POST /internal/impersonation/handoff). A replayed URL
 * answers 410 instead of signing in again.
 */
export const IMPERSONATION_CODE_URL_PARAM = "impersonation_code";

/**
 * The legacy handoff: `${ADMIN_URL}?impersonation_token=<jwt>`. The token
 * itself rides the URL, so re-opening it re-authenticates (OPS-3.5). Still
 * accepted for one release so an Ops Panel deployed before the code handoff
 * keeps working; remove once every Ops deployment sends the code.
 */
export const IMPERSONATION_TOKEN_URL_PARAM = "impersonation_token";

/** Every URL param that starts an Ops Panel handoff. */
export const IMPERSONATION_HANDOFF_URL_PARAMS = [
  IMPERSONATION_CODE_URL_PARAM,
  IMPERSONATION_TOKEN_URL_PARAM,
] as const;

/** Tab-scoped (sessionStorage) key the auth store persists to in a support-mode tab. */
export const IMPERSONATION_AUTH_STORAGE_KEY = "palate-auth-impersonation";

/**
 * True when `token` is an Ops Panel impersonation JWT (`type: "impersonation"`).
 * The JWT itself is the source of truth for "is this a support-mode session"
 * (Bug-143) — there is no separate store flag.
 */
export function isImpersonationToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as { type?: unknown } | null;
    return payload?.type === "impersonation";
  } catch {
    return false;
  }
}

/**
 * True when this page load belongs to a support-mode tab:
 *   - first load of the handoff: the URL still carries `?impersonation_code`
 *     (or the legacy `?impersonation_token`) — root.tsx's AuthInitializer
 *     strips it before anything else. Hydrating a shared key here would load
 *     the operator's own session — refresh token included — or their own
 *     drafts into a tab that is about to become a different restaurant.
 *   - later in the same tab (the param is gone), a hard reload, or a browser
 *     "Duplicate tab" (which copies sessionStorage): the tab-scoped auth key
 *     still holds the impersonation token.
 * A tab-scoped key that no longer holds an impersonation token (support mode
 * was exited, the handoff code was refused, or the handoff's /me failed) is an
 * ordinary tab again.
 */
export function isImpersonationTab(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const search = new URLSearchParams(window.location.search);
    if (IMPERSONATION_HANDOFF_URL_PARAMS.some((param) => search.has(param))) {
      return true;
    }
    const raw = sessionStorage.getItem(IMPERSONATION_AUTH_STORAGE_KEY);
    if (!raw) return false;
    const persisted = JSON.parse(raw) as { state?: { token?: unknown } } | null;
    const token = persisted?.state?.token;
    return typeof token === "string" && isImpersonationToken(token);
  } catch {
    // Storage disabled / corrupt entry: nothing tab-scoped to resume.
    return false;
  }
}
